// IndexedDB-Layer für Offline-Trail-Cache und später Ride-Recordings.
// `idb` ist ein sehr dünner Promise-Wrapper um IndexedDB (~1 KB).

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { CruxPoint, GpxTrack } from '@adtu/shared';

interface AdtDb extends DBSchema {
  trails: {
    key: string;           // trail id
    value: {
      id: string;
      name: string;
      difficulty: string;
      trailType: string;
      colorHex?: string | null;
      description?: string | null;
      photos?: string[];
      gpxFilename: string;
      gpxRaw: string;      // kompletter GPX als String (klein nach Simplify)
      track: GpxTrack;     // geparsed und simplified
      // Schlüsselstellen vom Admin gesetzt — Distanz-basiert (s. CruxPoint).
      // Optional damit alte gecachte Einträge ohne das Feld weiter funktionieren;
      // bei jedem Trail-Re-Sync wird es auf [] normalisiert.
      cruxPoints?: CruxPoint[];
      cachedAt: string;    // ISO
    };
    indexes: { 'by-updated': string };
  };
  settings: {
    key: string;
    value: unknown;
  };
  rides: {
    key: string;           // local ride id
    value: {
      id: string;
      startedAt: string;
      endedAt?: string;
      points: Array<{ lat: number; lon: number; ele?: number; t: number; speed?: number }>;
      // Wave 14cg Part 2: cross-device-Sync-Felder.
      //   updatedAt — bei jedem lokalen Mutate (stop/rename/discard via
      //               soft-delete-on-server) gebumpt. Pull-Merge nutzt das
      //               für last-write-wins.
      //   syncedAt  — ISO des letzten erfolgreichen PUT. Wenn syncedAt
      //               unter updatedAt liegt (oder fehlt), gilt die Ride
      //               als "pending sync".
      // Beide optional damit alte (pre-14cg) Rows ohne Crash weiterleben;
      // beim ersten flush/push werden sie hochgezogen.
      updatedAt?: string;
      syncedAt?: string;
    };
  };
  // Per-Trail SRTM-Höhen-Samples als Fallback wenn die GPX keine ele hat.
  // Wir speichern nur die Sample-Werte (samples: number[]) + Metadaten — die
  // passenden Distanzen werden deterministisch aus dem Track berechnet
  // (sampleDistances(total, samples.length)), d.h. wir persistieren sie
  // nicht doppelt. Key = trail-id.
  //
  // Wave 14bk: `smoothingVersion` ist ein Cache-Buster fürs Moving-Average-
  // Smoothing. Alte Einträge ohne das Feld (oder mit abweichender Version)
  // werden in loadCachedSrtm verworfen, damit nach einem Algorithmus-Update
  // alle Trails mit dem neuen Smoothing neu gesampled/geglättet werden —
  // ohne dass wir die DB-Schema-Version hochziehen müssen. Optional damit
  // Legacy-Reads (vor 14bk) nicht an fehlendem Feld scheitern.
  trailElevations: {
    key: string;
    value: {
      trailId: string;
      samples: number[];      // Höhen in Metern, geglättet, gleichmäßig entlang Track-Länge
      source: 'srtm';         // später evtl. 'manual' etc.
      computedAt: string;     // ISO
      trackLengthMeters: number; // zur Validierung bei Trail-Updates
      smoothingVersion?: number; // Wave 14bk: s.o., undefined = pre-14bk
    };
  };
  // POIs (Points of Interest) — v5-Migration: vom Row-pro-POI-Modell zum
  // EIN-Blob-pro-Kategorie-Modell.
  //
  // Historie: bis v4 hatten wir `pois` mit N Rows pro Kategorie (500-2000
  // Trinkwasser-Punkte in Liguria). iOS Safari ist bei großen IDB-
  // Transaktionen mit vielen Puts anfällig für "database connection is
  // closing" und "indexed database server lost"-Fehler (User-Report iPad,
  // Batch 8). Die Lösung: eine Kategorie = eine IDB-Row die ein POI-Array
  // enthält. Kein Index nötig (wir laden sowieso eine ganze Kategorie auf
  // einmal), die Liste wird client-side gefiltert (viewport culling).
  //
  // Format der Row entspricht 1:1 der Server-Response von GET
  // /api/pois/:category — der Rider kann sie nach dem Fetch direkt ohne
  // Transform in IDB ablegen. Beim Offline-Read lesen wir die eine Row,
  // und das Array pois ist sofort einsatzbereit.
  poiBlobs: {
    key: string;              // category-id, z.B. 'drinking_water'
    value: {
      category: string;
      count: number;
      cachedAt: string;       // ISO, vom Server gesetzt
      pois: Array<{
        id: string;           // `${category}:${osmId}`
        category: string;
        lat: number;
        lon: number;
        name?: string | null;
        osmId: number;
        tags?: Record<string, string>;
        cachedAt: string;
      }>;
    };
  };
  // Offline-Queue für Admin-Crux-Edits (User-Wunsch Batch 7): wenn der User
  // auf dem Trail offline einen Crux setzt, schreiben wir ihn trotzdem sofort
  // in die lokale trails-Row (optimistisch) und parken hier den letzten
  // "zu pushenden Stand" pro Trail. Bei Netz + gültigem Token wird der
  // flushCruxQueue-Helper aktiv, schickt PATCH und löscht den Eintrag wieder.
  // Key = trailId, Value = volle neue cruxPoints-Liste plus Metadaten. Die
  // Liste ist bewusst vollständig (nicht inkrementell) — das Backend setzt
  // cruxPoints atomar, damit ist ein einfaches "last write wins"-Merge sicher.
  cruxQueue: {
    key: string;
    value: {
      trailId: string;
      cruxPoints: CruxPoint[];
      queuedAt: string;    // ISO
      attempts: number;    // zählt fehlgeschlagene Flush-Versuche
      lastError?: string;  // letzte Fehlermeldung, für Debug-Ausgaben
    };
  };
  // Offline-Queue für Elevation-Pushes (Wave 14bq, User-Report "wenn ich am
  // handy ein höhenprofil erzeuge landet er nicht IM BACKEND → anderes gerät
  // muss neu rechnen"). Analog zu cruxQueue: wenn der Push aus saveCachedSrtm
  // fehlschlägt (offline, Server down, Timeout), parken wir hier die volle
  // Elevation-Payload pro Trail. flushElevationQueue() retried beim nächsten
  // App-Start und bei jedem 'online'-Event. Key = trailId, Wert enthält die
  // komplette Payload die POST /api/trails/:id/elevation-samples erwartet
  // (samples/smoothingVersion/trackLengthMeters), damit der Flush keinen Re-
  // Compute braucht — der wäre auf einem anderen Gerät eh nicht möglich.
  // Last-write-wins: re-enqueue überschreibt eine ältere queued Payload
  // (z. B. nach einem Rebuild).
  elevationQueue: {
    key: string;
    value: {
      trailId: string;
      samples: number[];
      smoothingVersion: number;
      trackLengthMeters: number;
      queuedAt: string;    // ISO
      attempts: number;    // zählt fehlgeschlagene Flush-Versuche
      lastError?: string;  // letzte Fehlermeldung, für Debug-Ausgaben
    };
  };
  // DH Runs (v6): geordnete Trail-Ketten mit eigener GPX. Schema spiegelt
  // `trails`: id-keyed, inkl. parse-fertiger GpxTrack + Raw-XML, damit wir
  // offline das Höhenprofil rendern können. `trailIds` ist Attribution (welche
  // Trails sind Teil dieses Runs), `gpxRaw` + `track` sind die maßgebliche
  // Geometrie.
  runs: {
    key: string;           // run id
    value: {
      id: string;
      name: string;
      difficulty: string;
      trailIds: string[];
      description?: string | null;
      gpxFilename: string;
      gpxRaw: string;
      track: GpxTrack;
      cachedAt: string;    // ISO (Backend updatedAt)
      // Wave 14dt: optionale Shuttle-Area-Zuordnung (2-Char-Code wie PL/ME).
      // IDB ignoriert undeclared Felder; ältere Runs ohne dieses Feld
      // bleiben gültig — wird beim nächsten syncRunCache ergänzt.
      shuttleAreaCode?: string | null;
    };
    indexes: { 'by-updated': string };
  };
  // Custom Trails (v8, Wave 14bv Stage 6j): vom User selbst im Create-
  // Custom-Flow erzeugte Routen. Device-local, kein Server-Sync — jeder
  // User hat seine eigenen Custom-Trails. Schema ähnlich zu `trails`, aber
  // mit weniger Metadaten (keine Crux-Points, kein Admin-Workflow). `coords`
  // sind die berechneten Route-Punkte aus dem Pin-Route/Finger-Route-
  // Response (Dijkstra-Output). Elevation/Profile wird später in Stage 7
  // nachgezogen (sample via MapLibre-DEM oder server-side sampling).
  customTrails: {
    key: string;           // client-generated id (timestamp + random slug)
    value: {
      id: string;
      name: string;                             // User-vergeben oder Auto-Default
      difficulty: string;                       // vorläufig 'unknown' bis User setzt
      // Wave 14cf-fix5: optionale dritte Komponente = Elevation in Metern.
      // Beim GPX-Import (oder von einer Routing-Engine die ele liefert)
      // persistiert wir die Höhe pro Punkt direkt in coords. useTrailElevation
      // erkennt das via hasElevationData() und überspringt den SRTM-Fallback —
      // das Profil bleibt damit so akkurat wie die Quelle (z.B. Komoot's
      // ~10 m Genauigkeit statt SRTM's 30 m + Cell-Drift).
      // Alte Einträge haben nur [lon, lat] — Reader behandeln coords[i][2]
      // als optional (undefined → kein ele für den Punkt).
      coords: Array<[number, number] | [number, number, number]>;
      lengthM: number;
      trailsUsed: string[];                     // referenzierte "echte" Trail-IDs
      trailIds?: string[];                      // ursprüngliche User-Auswahl (pin-mode)
      startPin?: [number, number];
      endPin?: [number, number];
      createdAt: string;                        // ISO
      // Wave 14cf-fix11: "Auf Karte anzeigen"-Toggle. Optional — alte
      // Einträge ohne das Feld sind effektiv showOnMap=false.
      showOnMap?: boolean;
      // Wave 14cf-fix12: km-Marker-Carousel. Optional — alte Einträge
      // sind effektiv 'off'.
      kmMarkers?: 'off' | '1km' | '10km';
      // Wave 14ca-fix64: pro-Segment-Trail-Mapping aus pin-route-response.
      // Optional — alte CustomTrails (vor 14ca-fix64) haben das Feld nicht.
      // Strukturell identisch zu CustomTrailRouteSegment in customTrails.ts.
      routeSegments?: Array<{
        kind: 'connector' | 'trail' | 'bridge';
        trailId: string | null;
        from: number;
        to: number;
      }>;
    };
    indexes: { 'by-created': string };
  };
  // User-marked POIs (v9, Wave 14cf-fix3): vom Rider selbst markierte Punkte
  // an seinem aktuellen GPS-Fix. Im Gegensatz zu poiBlobs (= OSM-Server-
  // Cache, read-only, eine Row pro Kategorie) speichern wir hier EINE Row
  // pro markiertem Punkt. Device-local, kein Server-Sync (vorerst — kann
  // später analog zur cruxQueue serverseitig nachgezogen werden).
  //
  // Feldsemantik:
  //   id        — client-generated, `${category}:${timestamp}-${rand}`
  //   category  — analog zu poiBlobs.category. Aktuell nur 'drinking_water';
  //               künftig 'cafe' / 'pranzo' / 'bike_shop' / 'beach' etc.
  //   note      — optionaler Freitext, vom User beim Markieren eingetippt.
  //   ele       — Höhe in Metern, falls Geolocation-API einen Wert lieferte.
  //               Für späteres Profil-Overlay nützlich, sonst kosmetisch.
  //   createdAt — ISO. Sortierung "neueste zuerst" auf Listings.
  //
  // Index by-created erlaubt die My-POIs-Ansicht in linearer Zeit zu
  // sortieren ohne alle Rows in JS zu laden + sortieren.
  userPois: {
    key: string;                                // client-generated id
    value: {
      id: string;
      category: string;                         // POI-Kategorie-Slug
      lat: number;
      lon: number;
      ele?: number | null;
      note?: string | null;
      createdAt: string;                        // ISO
    };
    indexes: { 'by-created': string };
  };
  // Admin-POI-Blob (v11, Wave 14cf-fix9): vom Server gefetched, alle
  // Admin-markierten POIs in einem Blob. Single-Row-Modell wie poiBlobs
  // (ein put() statt N puts → iOS-Safari-IDB-Quirk-resistant).
  // Key fix-string 'all' (statt category-pro-Row) — Admin-POIs sind
  // typischerweise wenige hundert Einträge, wir fetchen + cachen sie alle
  // zusammen und filtern client-side nach Kategorie wenn nötig.
  adminPoiBlob: {
    key: string;            // immer 'all'
    value: {
      key: string;          // 'all'
      cachedAt: string;     // ISO
      pois: Array<{
        id: string;
        category: string;
        lat: number;
        lon: number;
        ele?: number | null;
        note?: string | null;
        createdAt: string;
        createdByUserId?: string | null;
      }>;
    };
  };
  // Admin-POI-Push-Queue (v11): wenn Admin offline einen POI markiert,
  // landet die Payload hier bis der nächste Online-Tick sie pusht.
  // Analog zu cruxQueue. Last-write-wins per id.
  adminPoiQueue: {
    key: string;            // client-generated POI-id
    value: {
      id: string;
      category: string;
      lat: number;
      lon: number;
      ele?: number | null;
      note?: string | null;
      queuedAt: string;     // ISO
      attempts: number;
      lastError?: string;
    };
  };
  // Featured-Tour Per-User-Prefs (v12, Wave 14cd): Featured Tours selbst
  // sind shared (server-owned), aber jeder User entscheidet selbst was er
  // auf seiner Karte sehen will. Diese Tabelle hält die Sichtbarkeit +
  // km-Marker-Mode pro Featured-Tour-ID. Keine Tour-Geometrie hier — die
  // wird beim Render-Bedarf vom Server gefetched (analog runs.ts).
  featuredTourPrefs: {
    key: string;            // featured-tour id
    value: {
      tourId: string;
      showOnMap: boolean;
      kmMarkers?: 'off' | '1km' | '10km';
      updatedAt: string;    // ISO
    };
  };
  // Ride-Sync-Queue (v13, Wave 14cg Part 2): pending Pushes für Rides die
  // aufgenommen wurden während offline / Server unerreichbar / Push fail-
  // schlug. Analog cruxQueue/elevationQueue/adminPoiQueue. Key = ride-id;
  // bei jedem Re-Save (z.B. Rename, Combine-Resume) überschreibt sich der
  // Eintrag — letzter Stand wird gepusht (last-write-wins).
  //
  // Wir parken hier NICHT die volle Ride (die liegt im rides-Store), nur
  // einen Marker + Retry-State. Beim Flush liest der Sync die Ride aus
  // rides + pusht sie; bei Erfolg schreibt er syncedAt zurück in rides und
  // löscht den Queue-Eintrag.
  //
  // op: 'put' oder 'delete' — Soft-Delete: lokal löscht der User die Ride
  // aus dem rides-Store, hinterlässt aber einen 'delete'-Queue-Eintrag mit
  // dem nötigen Server-Roundtrip. Andernfalls würde eine pulled Ride von
  // einem anderen Gerät beim nächsten Sync wieder auftauchen.
  rideQueue: {
    key: string;
    value: {
      rideId: string;
      op: 'put' | 'delete';
      queuedAt: string;     // ISO
      attempts: number;     // gescheiterte Flush-Versuche
      lastError?: string;
    };
  };
}

let dbPromise: Promise<IDBPDatabase<AdtDb>> | null = null;

// v7: neuer Store 'elevationQueue' — offline-Queue für Elevation-Pushes.
//     User-Report Wave 14bq: "wenn ich am handy ein höhenprofil erzeuge,
//     landet es nicht IM BACKEND, also muss ich es am anderen gerät wieder
//     erzeugen." Ursache: pushTrailElevationSamples war fire-and-forget ohne
//     Queue/Retry. Crux-Queue-Pattern (v4) war da schon Goldstandard — dies
//     ist das 1:1-Äquivalent fürs Höhenprofil.
// v6: neuer Store 'runs' — DH-Runs als geordnete Trail-Ketten mit eigener
//     GPX. Spiegelt `trails`-Schema.
// v4: neuer Store 'cruxQueue' — offline-Queue für Admin-Crux-Edits (User
//     will crux auf'm Bike auch offline setzen können).
// v3: neuer Store 'pois' für OSM-POI-Cache (Trinkwasser, später mehr Kategorien).
// v2: neuer Store 'trailElevations' für SRTM-Fallback-Höhenprofile.
// Bei Upgrades werden bestehende Stores NICHT angefasst — idb's upgrade()
// migriert nur deltas, bestehende Stores bleiben intakt.
//
// iOS-Safari-Kontext (Batch 8, User-Report "Failed to execute 'transaction'
// on 'IDBDatabase': The database connection is closing."):
//   iOS Safari schließt IDB-Connections aggressiv, z.B. wenn die Page in den
//   Hintergrund geht ODER wenn eine lange async-Lücke zwischen IDB-Ops liegt
//   (bei uns: mehrere Sekunden Overpass-fetch zwischen clear+store). Unser
//   gecachtes dbPromise resolved dann zwar noch, aber .transaction() wirft
//   "connection is closing". Lösung:
//     1) Beim open() einen close-Handler registrieren der dbPromise auf null
//        setzt, damit der NÄCHSTE getDb() frisch öffnet.
//     2) openDB's `terminated` callback nullt ebenfalls (z.B. wenn der
//        Browser den Tab kickt wegen quota/etc).
//     3) Exportierter withDb()-Helper retried 1× mit frischer Connection
//        wenn er eine "closing"/"InvalidState"-Error sieht. Callsites in
//        heißen Pfaden (POI-Clear+Store, Trail-Writes) nutzen das.
function openAdtDb(): Promise<IDBPDatabase<AdtDb>> {
  const p = openDB<AdtDb>('all-day-turbo', 13, {
    upgrade(db, oldVersion) {
      if (!db.objectStoreNames.contains('trails')) {
        const trails = db.createObjectStore('trails', { keyPath: 'id' });
        trails.createIndex('by-updated', 'cachedAt');
      }
      if (!db.objectStoreNames.contains('settings')) {
        db.createObjectStore('settings');
      }
      if (!db.objectStoreNames.contains('rides')) {
        db.createObjectStore('rides', { keyPath: 'id' });
      }
      // v2: trailElevations.
      if (oldVersion < 2 && !db.objectStoreNames.contains('trailElevations')) {
        db.createObjectStore('trailElevations', { keyPath: 'trailId' });
      }
      // v3: pois (altes Row-pro-POI-Schema — wird in v5 durch poiBlobs ersetzt).
      // Wir legen ihn bei Fresh-Install NICHT mehr an (siehe v5). Bei upgrade
      // from 3/4 existiert er ggf. noch — v5 räumt ihn unten weg.
      // v4: cruxQueue.
      if (oldVersion < 4 && !db.objectStoreNames.contains('cruxQueue')) {
        db.createObjectStore('cruxQueue', { keyPath: 'trailId' });
      }
      // v6: DH-Runs-Store. Spiegelt `trails` (id-keyed + by-updated-Index)
      // damit wir offline auf die Run-GPX zugreifen können.
      if (oldVersion < 6 && !db.objectStoreNames.contains('runs')) {
        const runs = db.createObjectStore('runs', { keyPath: 'id' });
        runs.createIndex('by-updated', 'cachedAt');
      }
      // v7: elevationQueue. Offline-Retry fürs Höhenprofil-Push.
      if (oldVersion < 7 && !db.objectStoreNames.contains('elevationQueue')) {
        db.createObjectStore('elevationQueue', { keyPath: 'trailId' });
      }
      // v8: customTrails. User-erzeugte Routen aus dem Create-Custom-Flow.
      if (oldVersion < 8 && !db.objectStoreNames.contains('customTrails')) {
        const ct = db.createObjectStore('customTrails', { keyPath: 'id' });
        ct.createIndex('by-created', 'createdAt');
      }
      // v9: userPois — vom Rider selbst markierte POIs (Wave 14cf-fix3).
      // Eine Row pro markiertem Punkt. Index by-created für Sortierung.
      if (oldVersion < 9 && !db.objectStoreNames.contains('userPois')) {
        const up = db.createObjectStore('userPois', { keyPath: 'id' });
        up.createIndex('by-created', 'createdAt');
      }
      // v11: adminPoiBlob (Server-cached Admin-POIs, ein Blob)
      // + adminPoiQueue (Offline-Push-Queue, eine Row pro Pending-POI).
      // v10 bewusst übersprungen — keine Migration nötig, fix9 inkrementiert
      // direkt auf 11 für klare Lesbarkeit (admin-pois-Stores zusammen).
      if (oldVersion < 11) {
        if (!db.objectStoreNames.contains('adminPoiBlob')) {
          db.createObjectStore('adminPoiBlob', { keyPath: 'key' });
        }
        if (!db.objectStoreNames.contains('adminPoiQueue')) {
          db.createObjectStore('adminPoiQueue', { keyPath: 'id' });
        }
      }
      // v12: featuredTourPrefs (Wave 14cd) — per-user showOnMap +
      // kmMarkers für Featured Tours. Server-owned tour-Daten bleiben
      // in keinem IDB-Store; nur die client-private Sichtbarkeit lebt
      // hier. Keyed by tour-id, kein Index nötig (wir laden alle
      // Einträge zusammen wenn die Map die sichtbaren Tours zeichnen
      // soll).
      if (oldVersion < 12 && !db.objectStoreNames.contains('featuredTourPrefs')) {
        db.createObjectStore('featuredTourPrefs', { keyPath: 'tourId' });
      }
      // v13: rideQueue (Wave 14cg Part 2). Offline-Queue für Ride-Pushes.
      // Pattern identisch zu cruxQueue/elevationQueue/adminPoiQueue:
      // ein Eintrag pro pending op, last-write-wins per id.
      if (oldVersion < 13 && !db.objectStoreNames.contains('rideQueue')) {
        db.createObjectStore('rideQueue', { keyPath: 'rideId' });
      }
      // v5: POI-Schema-Migration auf Ein-Blob-pro-Kategorie.
      // Motivation: iOS Safari kippt bei großen Multi-Put-TX. Wir lösen
      // das indem wir den alten N-Row-`pois`-Store wegwerfen und durch
      // `poiBlobs` ersetzen, das pro Kategorie nur EINE Row hat. Der erste
      // Download nach Update holt den kompletten Blob vom Backend.
      if (oldVersion < 5) {
        // `pois` ist nicht mehr im Schema-Interface (AdtDb), deshalb muss der
        // Legacy-Delete hier über einen any-Cast — wir sprechen zur Laufzeit
        // trotzdem das echte IDB-API an und räumen den alten Store sauber weg.
        const rawDb = db as unknown as IDBDatabase;
        if (rawDb.objectStoreNames.contains('pois')) {
          rawDb.deleteObjectStore('pois');
        }
        if (!db.objectStoreNames.contains('poiBlobs')) {
          db.createObjectStore('poiBlobs', { keyPath: 'category' });
        }
      }
    },
    // Firefox/WebKit feuern `terminated` wenn die Connection ausserhalb
    // unserer Kontrolle geschlossen wird (OS-kill, private-mode-quota,
    // VersionChange von anderem Tab). Wir resetten das cached Promise
    // damit der nächste Aufruf frisch öffnet statt auf der toten Referenz
    // zu scheitern.
    terminated() {
      dbPromise = null;
    },
  });
  // Zusätzlich: Wenn die DB explizit geschlossen wird (auch via OS), feuert
  // das native IDBDatabase ein 'close'-Event. idb forwarded das auf die
  // resolved DB. Wir hängen den Handler erst nach resolve dran.
  void p.then((db) => {
    db.addEventListener('close', () => {
      dbPromise = null;
    });
  }).catch(() => {
    // Wenn openDB selbst scheitert: dbPromise nullen damit der nächste
    // getDb() neu versucht (statt das gescheiterte Promise ewig zu cachen).
    dbPromise = null;
  });
  return p;
}

export function getDb() {
  if (!dbPromise) {
    dbPromise = openAdtDb();
  }
  return dbPromise;
}

/**
 * iOS-resilient IDB-Operation-Wrapper. Führt `fn(db)` aus; wenn dabei ein
 * "connection is closing" / "IDB server lost" / InvalidStateError-Fehler
 * auftritt (passiert auf iOS Safari nach langen async-Lücken, z.B. multi-
 * sekunden Overpass-Fetch, oder wenn iOS den IDB-Server-Prozess killt),
 * wird das cached dbPromise resettet und mit kurzem Delay + frischer
 * Connection bis zu 2× neu versucht.
 *
 * Warum 2 Retries statt 1: auf dem iPad kann iOS's IDB-Server-Prozess
 * länger brauchen bis er wiederbelebt ist. 1 Retry reicht für den
 * "connection closing"-Fall, aber bei "server lost" braucht's manchmal
 * einen Tick mehr.
 *
 * Wichtig: Bei echten Fehlern (quota exceeded, upgrade needed, etc.)
 * eskalieren wir sofort statt unendlich zu loopen.
 */
export async function withDb<T>(
  fn: (db: IDBPDatabase<AdtDb>) => Promise<T>,
): Promise<T> {
  let lastErr: unknown = null;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const db = await getDb();
      return await fn(db);
    } catch (err) {
      lastErr = err;
      if (!isClosedConnectionError(err)) throw err;
      // Cached Promise wegwerfen, kurz warten (iOS braucht manchmal
      // einen Tick bis der IDB-Server-Prozess wieder antwortet), dann
      // nochmal mit frischer Connection probieren.
      dbPromise = null;
      if (attempt < 2) {
        await new Promise((r) => setTimeout(r, 120 * (attempt + 1)));
      }
    }
  }
  throw lastErr;
}

/**
 * Erkennt die iOS-Safari-"IDB kaputt"-Klasse von Fehlern. Es gibt mehrere
 * Varianten:
 *
 *   - "The database connection is closing."
 *     → iOS hat die einzelne Connection geschlossen (z.B. nach async-Gap).
 *   - "Connection to Indexed Database server lost. Refresh the page to try again."
 *     → iOS hat den ganzen IDB-Server-Prozess gekillt (z.B. Memory-Pressure).
 *   - InvalidStateError (DOMException) → bei .transaction() auf closed DB.
 *   - "IDBDatabase is closed" / "The database has been closed."
 *     → andere Browser-Varianten.
 *
 * Wir matchen großzügig über mehrere Keywords damit kein Fall durchrutscht.
 */
export function isClosedConnectionError(err: unknown): boolean {
  if (!err) return false;
  const name = (err as { name?: string }).name ?? '';
  const msg = err instanceof Error ? err.message : String(err);
  if (name === 'InvalidStateError') return true;
  // Vollständige Phrasen matchen (kein Keyword-AND-Chaos) — jede Zeile ist
  // eine echte iOS/Safari/Chrome/FF-Fehlermeldung die wir beobachtet oder
  // aus Bugtrackern gefischt haben.
  const patterns = [
    /database connection is closing/i,
    /database is closing/i,
    /database has been closed/i,
    /idbdatabase.+closed/i,
    /idbdatabase.+closing/i,
    /connection to (indexed)?\s*database server lost/i,
    /connection to idb server/i,
    /transaction.+idbdatabase.+(closing|closed)/i,
  ];
  return patterns.some((re) => re.test(msg));
}
